import { ProfileValueOption, ProfileFields } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { ResolverFunction } from './resolvers.config';
import { FieldConfig, ProfilePageConfig } from '../types/profile.types';
import { createFieldConfig } from '../utils/fields.helpers';
import {
  mapStandardProfileArrayValues,
  mapStandardProfileSingleValue,
} from '../utils/resolvers.helpers';

export const HEALTH_FIELD_CONFIGS: Partial<FieldConfig> = {
  demographicsReligions: createFieldConfig({
    key: 'demographicsReligions',
    type: { variant: 'SINGLE_SELECT' },
    placeholder: 'Select Religion/Spiritual Belief',
  }),
  healthMentalHealthInterests: createFieldConfig({
    key: 'healthMentalHealthInterests',
    type: { variant: 'AUTOCOMPLETE', manualFilterOptions: true },
    chipType: 'pill',
    showSearch: true,
    placeholder: 'Search Mental Health Interests',
  }),
  healthInterests: createFieldConfig({
    key: 'healthInterests',
    type: { variant: 'AUTOCOMPLETE', manualFilterOptions: true },
    chipType: 'pill',
    showSearch: true,
    placeholder: 'Search Health Interests',
  }),
  healthFoodNutrition: createFieldConfig({
    key: 'healthFoodNutrition',
    type: { variant: 'AUTOCOMPLETE', manualFilterOptions: true },
    chipType: 'pill',
    showSearch: true,
    placeholder: 'Search Health Food & Nutrition',
  }),
  healthExperiences: createFieldConfig({
    key: 'healthExperiences',
    type: { variant: 'CHECKBOX_GROUP' },
  }),
};

export const HEALTH_PAGE_CONFIG: ProfilePageConfig = {
  title: 'Health & Wellness',
  description: 'Add your personal health and well-being practices.',
  sections: [
    {
      title: 'Religion/Spiritual Beliefs',
      keys: ['demographicsReligions'],
    },
    {
      title: 'Mental/Spiritual Health',
      keys: ['healthMentalHealthInterests'],
      description: 'What do you practice to stay well?',
      hideDivider: true,
    },
    {
      title: 'Physical Activities',
      keys: ['healthInterests'],
      description: 'How do you stay active?',
    },
    {
      title: 'Food & Nutrition',
      keys: ['healthFoodNutrition'],
      description: 'What are your dietary practices?',
    },
    {
      title: 'Health Experiences',
      keys: ['healthExperiences'],
      description: 'Select all that apply to you.',
    },
  ],
};

export const HEALTH_HELPERS: Partial<
  Record<ProfileFields, ResolverFunction<string | number | ProfileValueOption>>
> = {
  demographicsReligions: (profile, options) =>
    mapStandardProfileSingleValue(
      profile.profile.demographicsReligions,
      options.demographicsReligions,
    ),

  healthMentalHealthInterests: (profile) =>
    (profile.profile.healthMentalHealthInterests ?? [])
      .map((interest) => (typeof interest === 'string' ? interest : interest.name))
      .filter(Boolean),

  healthInterests: (profile, options) =>
    (profile.profile.healthInterests ?? [])
      .map((interest) => {
        if (typeof interest === 'string') return interest;

        return options.healthInterests.find((opt) => opt.id === interest.id)?.name ?? interest.name;
      })
      .filter(Boolean),

  healthFoodNutrition: (profile, options) =>
    (profile.profile.healthFoodNutrition ?? [])
      .map((nutrition) => {
        if (typeof nutrition === 'string') return nutrition;

        return (
          options.healthFoodNutrition.find((opt) => opt.id === nutrition.id)?.name ?? nutrition.name
        );
      })
      .filter(Boolean),

  healthExperiences: (profile, options) =>
    mapStandardProfileArrayValues(profile.profile.healthExperiences, options.healthExperiences),
};
